import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import api from "../../utills/api";

const TOKEN_KEY = "superadminToken";

const PLATFORM_MODULES = [
  ["leadManagement", "Lead Management"],
  ["campaignManagement", "Campaign Management"],
  ["metaIntegration", "Meta Integration"],
  ["googleIntegration", "Google Integration"],
  ["youtubeIntegration", "YouTube Integration"],
  ["whatsapp", "WhatsApp"],
  ["analytics", "Analytics"],
  ["followUps", "Follow-ups"],
  ["counselling", "Counselling"],
  ["applications", "Applications"],
  ["admissions", "Admissions"],
];

const initialClient = {
  organizationName: "",
  legalBusinessName: "",
  clientCode: "",
  clientType: "Education",
  organizationType: "School",
  website: "",
  logoUrl: "",

  primaryContact: {
    name: "",
    email: "",
    phone: "",
    alternatePhone: "",
    designation: "",
  },

  address: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "India",
    postalCode: "",
  },

  billing: {
    plan: "Starter",
    billingCycle: "Monthly",
    price: "",
    currency: "INR",
    gstNumber: "",
    billingEmail: "",
    billingAddress: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      country: "India",
      postalCode: "",
    },
  },

  subscription: {
    status: "Trial",
    startDate: new Date()
      .toISOString()
      .slice(0, 10),
    trialEndDate: "",
    endDate: "",
    maxUsers: 5,
    maxLeads: 0,
    maxCampaigns: 0,
  },

  modules: {
    leadManagement: true,
    campaignManagement: true,
    metaIntegration: false,
    googleIntegration: false,
    youtubeIntegration: false,
    whatsapp: false,
    analytics: true,
    followUps: true,
    counselling: true,
    applications: true,
    admissions: true,
  },

  tenantAdmin: {
    name: "",
    email: "",
    phone: "",
    welcomeEmailRequested: true,
    forcePasswordChange: true,
  },

  notes: "",
};

const SuperAdminDashboard = () => {
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState([]);
  const [pagination, setPagination] =
    useState({
      page: 1,
      limit: 20,
      total: 0,
      pages: 1,
    });

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const [loading, setLoading] = useState(true);
  const [clientsLoading, setClientsLoading] =
    useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [createdCredentials, setCreatedCredentials] = useState(null);

  const [showCreate, setShowCreate] =
    useState(false);
  const [clientForm, setClientForm] =
    useState(initialClient);

  const token =
    localStorage.getItem(TOKEN_KEY);

  useEffect(() => {
    if (!token) {
      navigate(
        "/superadmin/login",
        { replace: true }
      );
    }
  }, [navigate, token]);

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  const fetchDashboard = useCallback(
    async () => {
      try {
        setLoading(true);

        const response =
          await api.get(
            "/superadmin/dashboard",
            authConfig
          );

        if (!response?.data?.success) {
          throw new Error(
            response?.data?.message ||
              "Unable to load dashboard."
          );
        }

        setDashboard(
          response.data.data
        );
      } catch (err) {
        if (
          err?.response?.status ===
          401
        ) {
          localStorage.removeItem(
            TOKEN_KEY
          );
          navigate(
            "/superadmin/login",
            { replace: true }
          );
          return;
        }

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load dashboard."
        );
      } finally {
        setLoading(false);
      }
    },
    [authConfig, navigate]
  );

  const fetchClients =
    useCallback(async () => {
      try {
        setClientsLoading(true);

        const response =
          await api.get(
            "/superadmin/clients",
            {
              ...authConfig,
              params: {
                page: pagination.page,
                limit: 20,
                search:
                  search.trim() ||
                  undefined,
                status:
                  status || undefined,
              },
            }
          );

        if (!response?.data?.success) {
          throw new Error(
            response?.data?.message ||
              "Unable to load clients."
          );
        }

        setClients(
          response.data?.data
            ?.clients || []
        );

        setPagination(
          response.data?.data
            ?.pagination || pagination
        );
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load clients."
        );
      } finally {
        setClientsLoading(false);
      }
    }, [
      authConfig,
      pagination.page,
      search,
      status,
    ]);

  useEffect(() => {
    if (token) {
      fetchDashboard();
    }
  }, [fetchDashboard, token]);

  useEffect(() => {
    if (token) {
      fetchClients();
    }
  }, [fetchClients, token]);

  const updateClientField = (
    field,
    value
  ) => {
    setClientForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updateNested = (
    section,
    field,
    value
  ) => {
    setClientForm((previous) => ({
      ...previous,
      [section]: {
        ...previous[section],
        [field]: value,
      },
    }));
  };

  const updateBillingAddress = (
    field,
    value
  ) => {
    setClientForm((previous) => ({
      ...previous,
      billing: {
        ...previous.billing,
        billingAddress: {
          ...previous.billing
            .billingAddress,
          [field]: value,
        },
      },
    }));
  };

  const toggleModule = (
    field
  ) => {
    setClientForm((previous) => ({
      ...previous,
      modules: {
        ...previous.modules,
        [field]:
          !previous.modules[field],
      },
    }));
  };

  const resetCreate = () => {
    setClientForm({
      ...initialClient,
      primaryContact: {
        ...initialClient.primaryContact,
      },
      address: {
        ...initialClient.address,
      },
      billing: {
        ...initialClient.billing,
        billingAddress: {
          ...initialClient.billing
            .billingAddress,
        },
      },
      subscription: {
        ...initialClient.subscription,
      },
      modules: {
        ...initialClient.modules,
      },
      tenantAdmin: {
        ...initialClient.tenantAdmin,
      },
    });
  };

  const createClient = async (
    event
  ) => {
    event.preventDefault();

    if (
      !clientForm.organizationName.trim() ||
      !clientForm.clientCode.trim()
    ) {
      setError(
        "Organization name and client code are required."
      );
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const response =
        await api.post(
          "/superadmin/clients",
          {
            ...clientForm,
            billing: {
              ...clientForm.billing,
              price: Number(
                clientForm.billing.price ||
                  0
              ),
            },
            subscription: {
              ...clientForm.subscription,
              maxUsers: Number(
                clientForm.subscription.maxUsers ||
                  1
              ),
              maxLeads: Number(
                clientForm.subscription.maxLeads ||
                  0
              ),
              maxCampaigns: Number(
                clientForm.subscription.maxCampaigns ||
                  0
              ),
            },
          },
          authConfig
        );

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message ||
            "Unable to create client."
        );
      }

      setCreatedCredentials(
        response.data?.data?.onboarding || null
      );

      setSuccess(
        "Client, organization and admin account created successfully."
      );

      setShowCreate(false);
      resetCreate();

      await Promise.all([
        fetchDashboard(),
        fetchClients(),
      ]);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to create client."
      );
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (
    tenantId,
    nextStatus
  ) => {
    try {
      setError("");

      const response =
        await api.put(
          `/superadmin/clients/${tenantId}/status`,
          { status: nextStatus },
          authConfig
        );

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message ||
            "Unable to update client status."
        );
      }

      await Promise.all([
        fetchDashboard(),
        fetchClients(),
      ]);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to update client status."
      );
    }
  };

  const logout = () => {
    localStorage.removeItem(
      TOKEN_KEY
    );
    navigate(
      "/superadmin/login",
      { replace: true }
    );
  };

  const widgets =
    dashboard?.widgets || {};

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Superadmin Dashboard"
          pageTitle="Administration"
        />

        {error && (
          <Alert
            color="danger"
            toggle={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            color="success"
            toggle={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {createdCredentials && (
          <Alert
            color="info"
            className="border-info-subtle"
            toggle={() => setCreatedCredentials(null)}
          >
            <div className="fw-semibold mb-2">
              Admin onboarding credentials
            </div>
            <div className="small mb-2">
              Save these credentials now. The temporary password is not stored in the Tenant record.
            </div>

            <Row className="g-3">
              <Col md={3}>
                <div className="text-muted fs-12">Tenant ID</div>
                <code>{createdCredentials.tenantId}</code>
              </Col>

              <Col md={3}>
                <div className="text-muted fs-12">Organization ID</div>
                <code>{String(createdCredentials.organizationId || "")}</code>
              </Col>

              <Col md={3}>
                <div className="text-muted fs-12">Admin Email</div>
                <div className="fw-semibold">{createdCredentials.adminEmail}</div>
              </Col>

              <Col md={3}>
                <div className="text-muted fs-12">Temporary Password</div>
                <code>{createdCredentials.temporaryPassword}</code>
              </Col>
            </Row>

            <div className="mt-2 small text-muted">
              Admin login: <strong>/login</strong> · Force password change: {createdCredentials.forcePasswordChange ? "Yes" : "No"}
            </div>
          </Alert>
        )}

        <Row className="mb-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h4 className="mb-1">
                  Tenant Management
                </h4>
                <p className="text-muted mb-0">
                  Manage clients, subscriptions,
                  modules and tenant administration.
                </p>
              </div>

              <div className="d-flex gap-2">
                <Button
                  color="light"
                  onClick={() =>
                    Promise.all([
                      fetchDashboard(),
                      fetchClients(),
                    ])
                  }
                >
                  <i className="ri-refresh-line me-1" />
                  Refresh
                </Button>

                <Button
                  color="primary"
                  onClick={() =>
                    setShowCreate(true)
                  }
                >
                  <i className="ri-add-line me-1" />
                  Create Client
                </Button>

                <Button
                  color="light"
                  onClick={logout}
                >
                  <i className="ri-logout-box-line me-1" />
                  Logout
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {loading ? (
          <Card>
            <CardBody className="text-center py-5">
              <Spinner size="sm" className="me-2" />
              Loading dashboard...
            </CardBody>
          </Card>
        ) : (
          <Row>
            <Metric
              label="Total Clients"
              value={
                widgets.totalClients || 0
              }
              icon="ri-building-line"
              col="xl-3"
            />

            <Metric
              label="Active Clients"
              value={
                widgets.activeClients || 0
              }
              icon="ri-checkbox-circle-line"
              col="xl-3"
            />

            <Metric
              label="Trial Clients"
              value={
                widgets.trialClients || 0
              }
              icon="ri-time-line"
              col="xl-3"
            />

            <Metric
              label="Monthly Recurring Revenue"
              value={`₹${Number(
                widgets.monthlyRecurringRevenue ||
                  0
              ).toLocaleString("en-IN")}`}
              icon="ri-money-rupee-circle-line"
              col="xl-3"
            />

            <Metric
              label="Annual Recurring Revenue"
              value={`₹${Number(
                widgets.annualRecurringRevenue ||
                  0
              ).toLocaleString("en-IN")}`}
              icon="ri-line-chart-line"
              col="xl-3"
            />

            <Metric
              label="Suspended"
              value={
                widgets.suspendedClients || 0
              }
              icon="ri-pause-circle-line"
              col="xl-3"
            />

            <Metric
              label="Expired"
              value={
                widgets.expiredClients || 0
              }
              icon="ri-calendar-close-line"
              col="xl-3"
            />

            <Metric
              label="Total Leads"
              value={
                widgets.totalLeads || 0
              }
              icon="ri-user-add-line"
              col="xl-3"
            />
          </Row>
        )}

        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">
                  Clients
                </h5>
                <p className="text-muted mb-0">
                  {pagination.total || 0} total clients
                </p>
              </div>
            </div>

            <Row className="g-3 mb-3">
              <Col md={5}>
                <Label>Search</Label>
                <Input
                  value={search}
                  placeholder="Client, tenant ID, code or email..."
                  onChange={(event) => {
                    setSearch(
                      event.target.value
                    );
                    setPagination(
                      (previous) => ({
                        ...previous,
                        page: 1,
                      })
                    );
                  }}
                />
              </Col>

              <Col md={3}>
                <Label>Status</Label>
                <Input
                  type="select"
                  value={status}
                  onChange={(event) => {
                    setStatus(
                      event.target.value
                    );
                    setPagination(
                      (previous) => ({
                        ...previous,
                        page: 1,
                      })
                    );
                  }}
                >
                  <option value="">
                    All statuses
                  </option>
                  {[
                    "Trial",
                    "Active",
                    "Suspended",
                    "Expired",
                    "Cancelled",
                  ].map((item) => (
                    <option
                      key={item}
                      value={item}
                    >
                      {item}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>

            <div className="table-responsive">
              <Table
                hover
                className="align-middle table-nowrap"
              >
                <thead className="table-light">
                  <tr>
                    <th>Client</th>
                    <th>Tenant ID</th>
                    <th>Contact</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Price</th>
                    <th>Created</th>
                    <th className="text-end">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {clientsLoading ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="text-center py-4"
                      >
                        <Spinner
                          size="sm"
                          className="me-2"
                        />
                        Loading clients...
                      </td>
                    </tr>
                  ) : clients.length ===
                    0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="text-center py-5"
                      >
                        No clients found.
                      </td>
                    </tr>
                  ) : (
                    clients.map(
                      (client) => (
                        <tr
                          key={
                            client.tenantId
                          }
                        >
                          <td>
                            <div className="fw-semibold">
                              {
                                client.organizationName
                              }
                            </div>
                            <div className="text-muted fs-12">
                              {
                                client.clientCode
                              }
                            </div>
                          </td>

                          <td>
                            <code>
                              {
                                client.tenantId
                              }
                            </code>
                          </td>

                          <td>
                            <div>
                              {
                                client
                                  .primaryContact
                                  ?.name
                              }
                            </div>
                            <div className="text-muted fs-12">
                              {
                                client
                                  .primaryContact
                                  ?.email
                              }
                            </div>
                          </td>

                          <td>
                            {
                              client.billing
                                ?.plan
                            }
                          </td>

                          <td>
                            <Badge
                              color={
                                client
                                  .subscription
                                  ?.status ===
                                "Active"
                                  ? "success"
                                  : client
                                      .subscription
                                      ?.status ===
                                    "Trial"
                                  ? "warning"
                                  : "secondary"
                              }
                            >
                              {
                                client
                                  .subscription
                                  ?.status
                              }
                            </Badge>
                          </td>

                          <td>
                            ₹
                            {Number(
                              client.billing
                                ?.price || 0
                            ).toLocaleString(
                              "en-IN"
                            )}
                            <div className="text-muted fs-12">
                              {
                                client.billing
                                  ?.billingCycle
                              }
                            </div>
                          </td>

                          <td>
                            {client.createdAt
                              ? new Date(
                                  client.createdAt
                                ).toLocaleDateString(
                                  "en-IN"
                                )
                              : "-"}
                          </td>

                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-1">
                              {client
                                .subscription
                                ?.status ===
                                "Active" ? (
                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() =>
                                    updateStatus(
                                      client.tenantId,
                                      "Suspended"
                                    )
                                  }
                                >
                                  Suspend
                                </Button>
                              ) : (
                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() =>
                                    updateStatus(
                                      client.tenantId,
                                      "Active"
                                    )
                                  }
                                >
                                  Activate
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </Table>
            </div>

            {pagination.pages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                  Page{" "}
                  {pagination.page}{" "}
                  of{" "}
                  {pagination.pages}
                </div>

                <div className="d-flex gap-2">
                  <Button
                    color="light"
                    size="sm"
                    disabled={
                      pagination.page <= 1
                    }
                    onClick={() =>
                      setPagination(
                        (previous) => ({
                          ...previous,
                          page:
                            previous.page -
                            1,
                        })
                      )
                    }
                  >
                    Previous
                  </Button>

                  <Button
                    color="light"
                    size="sm"
                    disabled={
                      pagination.page >=
                      pagination.pages
                    }
                    onClick={() =>
                      setPagination(
                        (previous) => ({
                          ...previous,
                          page:
                            previous.page +
                            1,
                        })
                      )
                    }
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        <CreateClientModal
          isOpen={showCreate}
          toggle={() =>
            !saving &&
            setShowCreate(false)
          }
          form={clientForm}
          saving={saving}
          updateField={updateClientField}
          updateNested={updateNested}
          updateBillingAddress={
            updateBillingAddress
          }
          toggleModule={toggleModule}
          submit={createClient}
        />
      </Container>
    </div>
  );
};

const Metric = ({
  label,
  value,
  icon,
  col,
}) => (
  <Col
    xl={3}
    md={6}
  >
    <Card className="card-animate">
      <CardBody>
        <div className="d-flex justify-content-between">
          <div>
            <p className="text-muted mb-1">
              {label}
            </p>
            <h4 className="mb-0">
              {value}
            </h4>
          </div>

          <div className="avatar-sm">
            <span className="avatar-title bg-primary-subtle text-primary rounded">
              <i
                className={`${icon} fs-20`}
              />
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  </Col>
);

const CreateClientModal = ({
  isOpen,
  toggle,
  form,
  saving,
  updateField,
  updateNested,
  updateBillingAddress,
  toggleModule,
  submit,
}) => (
  <Modal
    isOpen={isOpen}
    toggle={toggle}
    size="xl"
    scrollable
    centered
    backdrop="static"
    modalClassName="create-client-modal"
  >
    <ModalHeader toggle={toggle}>
      Create Client
    </ModalHeader>

    <form onSubmit={submit}>
      <ModalBody
        style={{
          maxHeight: "calc(100vh - 220px)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Section title="Organization">
          <Row className="g-3">
            <Field
              md={6}
              label="Organization Name *"
              value={form.organizationName}
              onChange={(e) =>
                updateField(
                  "organizationName",
                  e.target.value
                )
              }
            />

            <Field
              md={6}
              label="Legal Business Name"
              value={form.legalBusinessName}
              onChange={(e) =>
                updateField(
                  "legalBusinessName",
                  e.target.value
                )
              }
            />

            <Field
              md={4}
              label="Client Code *"
              value={form.clientCode}
              onChange={(e) =>
                updateField(
                  "clientCode",
                  e.target.value
                )
              }
            />

            <Col md={4}>
              <Label>Client Type *</Label>
              <Input
                type="select"
                value={form.clientType}
                onChange={(e) =>
                  updateField(
                    "clientType",
                    e.target.value
                  )
                }
              >
                <option>
                  Education
                </option>
              </Input>
            </Col>

            <Col md={4}>
              <Label>Organization Type *</Label>
              <Input
                type="select"
                value={form.organizationType}
                onChange={(e) =>
                  updateField(
                    "organizationType",
                    e.target.value
                  )
                }
              >
                <option value="School">School</option>
                <option value="College">College</option>
              </Input>
            </Col>

            <Field
              md={4}
              label="Website"
              value={form.website}
              onChange={(e) =>
                updateField(
                  "website",
                  e.target.value
                )
              }
            />

            <Field
              md={12}
              label="Logo URL"
              value={form.logoUrl}
              onChange={(e) =>
                updateField(
                  "logoUrl",
                  e.target.value
                )
              }
            />
          </Row>
        </Section>

        <Section title="Primary Contact">
          <Row className="g-3">
            <Field
              md={6}
              label="Contact Name *"
              value={
                form.primaryContact.name
              }
              onChange={(e) =>
                updateNested(
                  "primaryContact",
                  "name",
                  e.target.value
                )
              }
            />

            <Field
              md={6}
              label="Email *"
              type="email"
              value={
                form.primaryContact.email
              }
              onChange={(e) =>
                updateNested(
                  "primaryContact",
                  "email",
                  e.target.value
                )
              }
            />

            <Field
              md={4}
              label="Phone *"
              value={
                form.primaryContact.phone
              }
              onChange={(e) =>
                updateNested(
                  "primaryContact",
                  "phone",
                  e.target.value
                )
              }
            />

            <Field
              md={4}
              label="Alternate Phone"
              value={
                form.primaryContact
                  .alternatePhone
              }
              onChange={(e) =>
                updateNested(
                  "primaryContact",
                  "alternatePhone",
                  e.target.value
                )
              }
            />

            <Field
              md={4}
              label="Designation"
              value={
                form.primaryContact
                  .designation
              }
              onChange={(e) =>
                updateNested(
                  "primaryContact",
                  "designation",
                  e.target.value
                )
              }
            />
          </Row>
        </Section>

        <Section title="Address">
          <Row className="g-3">
            <Field
              md={6}
              label="Address Line 1"
              value={form.address.line1}
              onChange={(e) =>
                updateNested(
                  "address",
                  "line1",
                  e.target.value
                )
              }
            />

            <Field
              md={6}
              label="Address Line 2"
              value={form.address.line2}
              onChange={(e) =>
                updateNested(
                  "address",
                  "line2",
                  e.target.value
                )
              }
            />

            <Field
              md={4}
              label="City"
              value={form.address.city}
              onChange={(e) =>
                updateNested(
                  "address",
                  "city",
                  e.target.value
                )
              }
            />

            <Field
              md={4}
              label="State"
              value={form.address.state}
              onChange={(e) =>
                updateNested(
                  "address",
                  "state",
                  e.target.value
                )
              }
            />

            <Field
              md={2}
              label="Country"
              value={form.address.country}
              onChange={(e) =>
                updateNested(
                  "address",
                  "country",
                  e.target.value
                )
              }
            />

            <Field
              md={2}
              label="Postal Code"
              value={
                form.address.postalCode
              }
              onChange={(e) =>
                updateNested(
                  "address",
                  "postalCode",
                  e.target.value
                )
              }
            />
          </Row>
        </Section>

        <Section title="Billing & Subscription">
          <Row className="g-3">
            <Field
              md={4}
              label="Plan *"
              value={form.billing.plan}
              onChange={(e) =>
                updateNested(
                  "billing",
                  "plan",
                  e.target.value
                )
              }
            />

            <Col md={4}>
              <Label>Billing Cycle *</Label>
              <Input
                type="select"
                value={
                  form.billing.billingCycle
                }
                onChange={(e) =>
                  updateNested(
                    "billing",
                    "billingCycle",
                    e.target.value
                  )
                }
              >
                <option>
                  Monthly
                </option>
                <option>
                  Quarterly
                </option>
                <option>
                  Annual
                </option>
              </Input>
            </Col>

            <Field
              md={4}
              label="Price *"
              type="number"
              min="0"
              value={form.billing.price}
              onChange={(e) =>
                updateNested(
                  "billing",
                  "price",
                  e.target.value
                )
              }
            />

            <Field
              md={4}
              label="GST Number"
              value={
                form.billing.gstNumber
              }
              onChange={(e) =>
                updateNested(
                  "billing",
                  "gstNumber",
                  e.target.value
                )
              }
            />

            <Field
              md={8}
              label="Billing Email"
              type="email"
              value={
                form.billing.billingEmail
              }
              onChange={(e) =>
                updateNested(
                  "billing",
                  "billingEmail",
                  e.target.value
                )
              }
            />

            <Col md={3}>
              <Label>Subscription Status *</Label>
              <Input
                type="select"
                value={
                  form.subscription.status
                }
                onChange={(e) =>
                  updateNested(
                    "subscription",
                    "status",
                    e.target.value
                  )
                }
              >
                <option>
                  Trial
                </option>
                <option>
                  Active
                </option>
                <option>
                  Suspended
                </option>
                <option>
                  Expired
                </option>
                <option>
                  Cancelled
                </option>
              </Input>
            </Col>

            <Field
              md={3}
              label="Start Date *"
              type="date"
              value={
                form.subscription.startDate
              }
              onChange={(e) =>
                updateNested(
                  "subscription",
                  "startDate",
                  e.target.value
                )
              }
            />

            <Field
              md={3}
              label="Trial End"
              type="date"
              value={
                form.subscription.trialEndDate
              }
              onChange={(e) =>
                updateNested(
                  "subscription",
                  "trialEndDate",
                  e.target.value
                )
              }
            />

            <Field
              md={3}
              label="End Date"
              type="date"
              value={
                form.subscription.endDate
              }
              onChange={(e) =>
                updateNested(
                  "subscription",
                  "endDate",
                  e.target.value
                )
              }
            />

            <Field
              md={4}
              label="Max Users"
              type="number"
              min="1"
              value={
                form.subscription.maxUsers
              }
              onChange={(e) =>
                updateNested(
                  "subscription",
                  "maxUsers",
                  e.target.value
                )
              }
            />

            <Field
              md={4}
              label="Max Leads (0 = unlimited)"
              type="number"
              min="0"
              value={
                form.subscription.maxLeads
              }
              onChange={(e) =>
                updateNested(
                  "subscription",
                  "maxLeads",
                  e.target.value
                )
              }
            />

            <Field
              md={4}
              label="Max Campaigns (0 = unlimited)"
              type="number"
              min="0"
              value={
                form.subscription.maxCampaigns
              }
              onChange={(e) =>
                updateNested(
                  "subscription",
                  "maxCampaigns",
                  e.target.value
                )
              }
            />
          </Row>
        </Section>

        <Section title="Tenant Administrator">
          <Row className="g-3">
            <Field
              md={6}
              label="Admin Name *"
              value={
                form.tenantAdmin.name
              }
              onChange={(e) =>
                updateNested(
                  "tenantAdmin",
                  "name",
                  e.target.value
                )
              }
            />

            <Field
              md={6}
              label="Admin Email *"
              type="email"
              value={
                form.tenantAdmin.email
              }
              onChange={(e) =>
                updateNested(
                  "tenantAdmin",
                  "email",
                  e.target.value
                )
              }
            />

            <Field
              md={4}
              label="Admin Phone"
              value={
                form.tenantAdmin.phone
              }
              onChange={(e) =>
                updateNested(
                  "tenantAdmin",
                  "phone",
                  e.target.value
                )
              }
            />

            <Col md={4}>
              <Label>Welcome Email</Label>
              <Input
                type="select"
                value={
                  form.tenantAdmin
                    .welcomeEmailRequested
                    ? "true"
                    : "false"
                }
                onChange={(e) =>
                  updateNested(
                    "tenantAdmin",
                    "welcomeEmailRequested",
                    e.target.value ===
                      "true"
                  )
                }
              >
                <option value="true">
                  Send
                </option>
                <option value="false">
                  Do not send
                </option>
              </Input>
            </Col>

            <Col md={4}>
              <Label>Force Password Change</Label>
              <Input
                type="select"
                value={
                  form.tenantAdmin
                    .forcePasswordChange
                    ? "true"
                    : "false"
                }
                onChange={(e) =>
                  updateNested(
                    "tenantAdmin",
                    "forcePasswordChange",
                    e.target.value ===
                      "true"
                  )
                }
              >
                <option value="true">
                  Yes
                </option>
                <option value="false">
                  No
                </option>
              </Input>
            </Col>
          </Row>
        </Section>

        <Section title="Modules">
          <Row className="g-3">
            {PLATFORM_MODULES.map(
              ([key, label]) => (
                <Col
                  md={4}
                  key={key}
                >
                  <div className="border rounded p-3 d-flex align-items-center justify-content-between">
                    <span>{label}</span>

                    <Input
                      type="switch"
                      checked={
                        Boolean(
                          form.modules[key]
                        )
                      }
                      onChange={() =>
                        toggleModule(
                          key
                        )
                      }
                    />
                  </div>
                </Col>
              )
            )}
          </Row>
        </Section>

        <Section title="Notes">
          <Label>Internal Notes</Label>
          <Input
            type="textarea"
            rows="3"
            value={form.notes}
            onChange={(e) =>
              updateField(
                "notes",
                e.target.value
              )
            }
          />
        </Section>
      </ModalBody>

      <ModalFooter>
        <Button
          color="light"
          type="button"
          onClick={toggle}
          disabled={saving}
        >
          Cancel
        </Button>

        <Button
          color="primary"
          type="submit"
          disabled={saving}
        >
          {saving && (
            <Spinner
              size="sm"
              className="me-2"
            />
          )}
          Create Client
        </Button>
      </ModalFooter>
    </form>
  </Modal>
);

const Section = ({
  title,
  children,
}) => (
  <div className="border rounded p-3 mb-4">
    <h6 className="mb-3">
      {title}
    </h6>
    {children}
  </div>
);

const Field = ({
  md = 6,
  label,
  type = "text",
  value,
  onChange,
}) => (
  <Col md={md}>
    <Label>{label}</Label>
    <Input
      type={type}
      value={value ?? ""}
      onChange={onChange}
    />
  </Col>
);

export default SuperAdminDashboard;
